import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { API } from '../api/comunApi'
import { endpointsArticleSearch } from '../api/endpoints'
import { TOKEN_KEY } from '../constantes/localStorageKeys'

/**
 * Interruptor del motor de búsqueda de artículos.
 *
 * El valor global vive en la tabla Configuraciones ('Buscador.Motor') y se
 * cambia con un UPDATE, sin desplegar. Encima, cada usuario puede forzar un
 * motor distinto desde su navegador, para que un tester pueda trabajar en
 * 'comparar' mientras el resto de la empresa sigue en 'legacy':
 *
 *   localStorage.setItem('palex.search.engine', 'comparar')
 *   localStorage.removeItem('palex.search.engine')
 *
 * Ante cualquier fallo (sin red, endpoint caído, storage bloqueado) se
 * devuelve 'legacy', que es el comportamiento de siempre.
 */

export const SEARCH_ENGINES = {
  LEGACY: 'legacy',
  COMPARE: 'comparar',
  V2: 'v2',
}

export const SEARCH_ENGINE_OVERRIDE_KEY = 'palex.search.engine'

const VALID_ENGINES = Object.values(SEARCH_ENGINES)

const isValidEngine = (value) => VALID_ENGINES.includes(value)

/** Lee el override local. Devuelve null si no hay o no es válido. */
export const readSearchEngineOverride = () => {
  try {
    const value = localStorage.getItem(SEARCH_ENGINE_OVERRIDE_KEY)
    return isValidEngine(value) ? value : null
  } catch (error) {
    // Modo privado o almacenamiento bloqueado: sin override.
    return null
  }
}

/** Fija el override local. Pasar null lo elimina. */
export const setSearchEngineOverride = (value) => {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(SEARCH_ENGINE_OVERRIDE_KEY)
      return true
    }
    if (!isValidEngine(value)) {
      return false
    }
    localStorage.setItem(SEARCH_ENGINE_OVERRIDE_KEY, value)
    return true
  } catch (error) {
    return false
  }
}

/**
 * Igual que useSearchEngine, pero devuelve también de dónde sale el valor:
 *
 *   override-local  localStorage de este navegador (gana sobre todo lo demás)
 *   usuario         fila Buscador.MotorPorUsuario
 *   global          fila Buscador.Motor
 *   defecto         no hay fila; se repliega a legacy
 *
 * Sirve para que la pantalla pueda decir por qué muestra lo que muestra, en
 * vez de obligar a mirar la consola cada vez que no coincide con lo esperado.
 */
export const useSearchEngineInfo = () => {
  const [info, setInfo] = useState(() => {
    const local = readSearchEngineOverride()
    return local
      ? { engine: local, origen: 'override-local', idUsuario: null }
      : { engine: SEARCH_ENGINES.LEGACY, origen: 'cargando', idUsuario: null }
  })

  // El servidor puede dar una respuesta distinta según quién pregunte
  // (fila Buscador.MotorPorUsuario). Al iniciar sesión hay que volver a
  // preguntar: en el primer render todavía no hay usuario.
  const idUsuario = useSelector((estado) =>
    estado.userProfile ? estado.userProfile.id_Usuario : null,
  )

  useEffect(() => {
    // El override local manda: si existe, no se pregunta al servidor.
    if (readSearchEngineOverride()) return undefined

    // Si hay sesión pero el perfil todavía no ha llegado, NO se pregunta aún.
    // El servidor respondería con el valor global (no sabe quién eres) y la
    // pantalla montaría el motor antiguo durante un instante, para cambiarlo
    // al llegar el perfil. En el Catálogo eso es un parpadeo; en el BackOffice
    // son peticiones de búsqueda que se lanzan y se tiran.
    const hayToken = (() => {
      try {
        return !!localStorage.getItem(TOKEN_KEY)
      } catch (error) {
        return false
      }
    })()

    if (hayToken && !idUsuario) return undefined

    // axios 0.21.4 no admite AbortController: la cancelación va por CancelToken.
    // Al cancelar, la promesa se rechaza, así que no hace falta un flag extra
    // para evitar el setState sobre un componente ya desmontado.
    const source = axios.CancelToken.source()

    // Timeout explícito: axios no trae ninguno por defecto, y las pantallas
    // de Productos y Catálogo esperan a saber qué motor toca antes de pintar.
    // Una petición colgada las dejaría bloqueadas indefinidamente.
    //
    // 2,5 s es de sobra: esto es un SELECT de una fila sobre una tabla de
    // configuración. Si tarda más, algo va mal y esperar no lo arregla; se
    // cae a legacy, que es el comportamiento de siempre.
    API.get(endpointsArticleSearch.ENGINE, {
      cancelToken: source.token,
      timeout: 2500,
    })
      .then((response) => {
        const datos = (response && response.data) || {}
        if (isValidEngine(datos.engine)) {
          setInfo({
            engine: datos.engine,
            origen: datos.origen || 'global',
            idUsuario: datos.id_Usuario || null,
          })
        }
      })
      .catch(() => {
        // Cancelado, sin red o endpoint caído: se queda en 'legacy'.
        setInfo({ engine: SEARCH_ENGINES.LEGACY, origen: 'defecto', idUsuario: null })
      })

    return () => source.cancel()
  }, [idUsuario])

  return info
}

export const useSearchEngine = () => useSearchEngineInfo().engine

export default useSearchEngine
